import { type CdkDragDrop } from '@angular/cdk/drag-drop';
import { addDays, addHours, addMinutes, differenceInDays, isDate, isSameDay } from 'date-fns';
import { produce } from 'immer';

import { shiftUtcBetweenCalendarTimezones } from '@blaze-runtime/engine-util';
import { type DatasetRow } from '@blaze-shared/schema-domain';

import { type Calendar, type CalendarEvent, type EventChangeEvent } from '../calendar.models';

export const DEFAULT_CALENDAR_NAME = 'Unassigned';
export const DEFAULT_CALENDAR_NAME_TOKEN = 'EMPTY_CALENDAR_NAME_TOKEN';

export function getEmptyCalendar(): Calendar {
  return {
    id: null,
    name: null,
    timeZone: null,
    events: {},
  };
}

export function getNewCalendarEvent(
  selectedCalendarDate: Date,
  defaultDurationMinutes: number,
): Partial<CalendarEvent> {
  const startDate = selectedCalendarDate ? new Date(selectedCalendarDate) : new Date();
  const onlyDate =
    selectedCalendarDate.getHours() === 0 &&
    selectedCalendarDate.getMinutes() === 0 &&
    selectedCalendarDate.getSeconds() === 0;
  startDate.setHours(onlyDate ? new Date().getHours() + 1 : startDate.getHours() + 1);
  startDate.setMinutes(0);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);

  const endDate = addMinutes(startDate, defaultDurationMinutes);

  return {
    startDate,
    endDate,
  };
}

export function getShouldShowTimezones(calendars: Calendar[]): boolean {
  let showTimezone = false;
  const timeZones = calendars.filter((c) => c.timeZone).map((c) => c.timeZone);
  for (let i = 1; i < timeZones.length; i++) {
    showTimezone = timeZones[i - 1] !== timeZones[i];

    if (showTimezone) {
      break;
    }
  }

  return showTimezone;
}

export function applyEventDrop(
  change: CdkDragDrop<any, any, CalendarEvent>,
  dataRow: DatasetRow | undefined,
  timeDelta: { h: number; m: number },
  props: {
    startField: string | undefined;
    endField: string | undefined;
    calendarField: string | undefined;
    calendarFieldType: 'String' | 'Tag' | 'Link' | undefined;
  },
): { updated: DatasetRow; sameDayViewChange: boolean } | undefined {
  if (!dataRow) {
    throw new Error('[Calendar] Event dataRow missing');
  }

  const event = change.item.data;
  const from = change.previousContainer.data;
  const to = change.container.data;
  const isDayChange = isDate(from) && isDate(to);
  let sameDayViewChange = false;
  // dropped into the same container
  if ((isDayChange && isSameDay(from, to)) || (!isDayChange && from.id === to.id)) {
    if (!timeDelta.h && !timeDelta.m) {
      return undefined;
    }
    sameDayViewChange = true;
  }

  const updated = produce(dataRow, (draft) => {
    if (!props.startField || !props.endField) {
      throw new Error('[Calendar] Missing fields configuration');
    }

    let since = new Date(draft[props.startField]);
    let until = new Date(draft[props.endField]);
    let rowValuesChanged = false;

    if (isDayChange) {
      const days = differenceInDays(to, from);
      event.startDate = applyTimeChange(addDays(event.startDate, days), timeDelta);
      event.endDate = applyTimeChange(addDays(event.endDate, days), timeDelta);
      since = addDays(since, days);
      until = addDays(until, days);
      rowValuesChanged = true;
    } else {
      const sourceCalendar = from as Calendar;
      const targetCalendar = to as Calendar;
      if (props.calendarField && sourceCalendar.id !== targetCalendar.id) {
        draft[props.calendarField] =
          props.calendarFieldType === 'Link'
            ? { rowId: targetCalendar.id, display: targetCalendar.name }
            : targetCalendar.name;

        if (sourceCalendar.timeZone !== targetCalendar.timeZone) {
          const shiftedSince = shiftUtcBetweenCalendarTimezones(
            since,
            sourceCalendar.timeZone,
            targetCalendar.timeZone,
          );
          if (shiftedSince.getTime() !== since.getTime()) {
            since = shiftedSince;
            until = shiftUtcBetweenCalendarTimezones(
              until,
              sourceCalendar.timeZone,
              targetCalendar.timeZone,
            );
            rowValuesChanged = true;
          }
        }
      }
    }

    if (rowValuesChanged || timeDelta.h || timeDelta.m) {
      draft[props.startField] = applyTimeChange(since, timeDelta);
      draft[props.endField] = applyTimeChange(until, timeDelta);
    }
  });

  return { updated, sameDayViewChange };
}

export function applyEventChange(
  change: EventChangeEvent,
  dataRow: DatasetRow | undefined,
  props: {
    startField: string | undefined;
    endField: string | undefined;
  },
): DatasetRow {
  if (!dataRow) {
    throw new Error('[Calendar] Event dataRow missing');
  }

  const { changeType, delta } = change;
  const updated = produce(dataRow, (draft) => {
    if (!props.startField || !props.endField) {
      throw new Error('[Calendar] Missing fields configuration');
    }

    if (changeType === 'start' || changeType === 'both') {
      draft[props.startField] = applyTimeChange(new Date(draft[props.startField]), delta);
    }

    if (changeType === 'end' || changeType === 'both') {
      draft[props.endField] = applyTimeChange(new Date(draft[props.endField]), delta);
    }
  });

  return updated;
}

function applyTimeChange(date: Date, delta: { h?: number; m?: number }): Date {
  if (delta.h) {
    date = addHours(date, delta.h);
  }
  if (delta.m) {
    date = addMinutes(date, delta.m);
  }
  return date;
}

export function pixelsToTime(delta: number, oneHourHeight: number): { h: number; m: number } {
  const r = delta % oneHourHeight;
  const h = (delta - r) / oneHourHeight;
  const m = Math.round((r / oneHourHeight) * 60);
  return { h, m };
}
